<template>
  <AnalyticsCard
    title="Booking activity"
    :subtitle="`Taken, completed and canceled per ${bucketWord}`"
    :loading="loading"
    :empty="isEmpty"
    empty-title="No booking activity in this period"
    empty-hint="Once bookings start coming in, this chart shows how the three of them move against each other."
    :height="288"
  >
    <template #actions>
      <span v-for="s in LEGEND" :key="s.label"
        class="hidden md:flex items-center gap-1.5 text-xs text-gray-500 dark:text-gray-400 ml-3">
        <span class="w-2.5 h-2.5 rounded-full inline-block" :style="{ backgroundColor: s.color }"></span>{{ s.label }}
      </span>
    </template>
    <div ref="el" class="w-full h-72"></div>
  </AnalyticsCard>
</template>

<script setup>
import { computed, toRef } from 'vue'
import AnalyticsCard from '../shared/AnalyticsCard.vue'
import { useAnalyticsChart, baseOptions, PALETTE, hexToRgba } from '../shared/useAnalyticsChart.js'

const props = defineProps({
  data:    { type: Array, default: () => [] },
  bucket:  { type: String, default: 'day' },
  loading: { type: Boolean, default: false },
})

const LEGEND = [
  { label: 'Taken',     color: PALETTE.blue },
  { label: 'Completed', color: PALETTE.green },
  { label: 'Canceled',  color: PALETTE.red },
]

const bucketWord = computed(() => ({ hour: 'hour', day: 'day', week: 'week', month: 'month' }[props.bucket] || 'day'))

const isEmpty = computed(() =>
  !props.loading && props.data.length > 0 &&
  props.data.every(d => !d.created && !d.completed && !d.canceled))

const { el } = useAnalyticsChart((_node, tokens) => {
  if (!props.data.length) return null
  const base = baseOptions(tokens)
  const labels = props.data.map(d => d.label)

  return {
    ...base,
    chart: { ...base.chart, type: 'areaspline', height: 288, spacing: [16, 16, 8, 4] },
    xAxis: {
      ...base.xAxis,
      categories: labels,
      // Roughly seven labels whatever the range, so a 12 month view does not
      // stack 365 dates on top of each other.
      tickInterval: Math.max(1, Math.ceil(labels.length / 7)),
      crosshair: { width: 1, color: tokens.line },
    },
    yAxis: { ...base.yAxis, min: 0, allowDecimals: false },
    tooltip: { ...base.tooltip, shared: true },
    plotOptions: {
      areaspline: {
        fillOpacity: 1,
        lineWidth: 2,
        marker: { enabled: false, symbol: 'circle', radius: 3, states: { hover: { enabled: true, lineWidth: 0 } } },
      },
    },
    series: [
      { name: 'Taken',     data: props.data.map(d => d.created),   color: PALETTE.blue,  fillColor: gradient(PALETTE.blue) },
      { name: 'Completed', data: props.data.map(d => d.completed), color: PALETTE.green, fillColor: gradient(PALETTE.green) },
      { name: 'Canceled',  data: props.data.map(d => d.canceled),  color: PALETTE.red,   fillColor: gradient(PALETTE.red) },
    ],
  }
}, [toRef(props, 'data')])

// A vertical fade rather than a flat wash: three overlapping flat fills at 7%
// opacity become an indistinguishable grey band wherever the series cross.
function gradient(color) {
  return {
    linearGradient: { x1: 0, y1: 0, x2: 0, y2: 1 },
    stops: [[0, hexToRgba(color, 0.22)], [1, hexToRgba(color, 0)]],
  }
}
</script>
